import { useCallback, useEffect, useRef, useState } from "react";
import { ShieldAlert, ShieldCheck, Loader2, Video as VideoIcon } from "lucide-react";

/**
 * JitsiCall — Jitsi/JaaS video component.
 *
 * Renders inside the same slot as `LiveKitCall` (which is why the prop
 * contract is identical) — `VideoCall.jsx` swaps between them based on
 * the server-declared `provider_id`.
 *
 * Loads `external_api.js` from the JaaS tenant lazily on mount (one
 * `<script>` tag per session; module-level cache prevents re-injection),
 * then instantiates `JitsiMeetExternalAPI(domain, options)` inside a ref'd
 * div. The `jwt` prop is the RS256 token minted by the backend
 * `JitsiProvider`; the `domain` prop is the JaaS join URL
 * (`https://8x8.vc/{app_id}`) — we split it into host + app_id here.
 *
 * Same imperative-API contract as LiveKitCall:
 *   onApiReady({ toggleAudio, toggleVideo, hangup })
 */

// ---- external_api.js loader (module-level, one-shot) --------------------
let _loaderPromise = null;
function loadExternalApi(scriptSrc) {
  if (typeof window === "undefined") return Promise.resolve(null);
  if (window.JitsiMeetExternalAPI) return Promise.resolve(window.JitsiMeetExternalAPI);
  if (_loaderPromise) return _loaderPromise;
  _loaderPromise = new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = scriptSrc;
    s.async = true;
    s.onload = () => resolve(window.JitsiMeetExternalAPI);
    s.onerror = () => {
      _loaderPromise = null;
      reject(new Error("failed to load Jitsi external_api.js"));
    };
    document.head.appendChild(s);
  });
  return _loaderPromise;
}

// Turn `https://8x8.vc/vpaas-magic-cookie-xxx` into
// {host: "8x8.vc", appId: "vpaas-magic-cookie-xxx", scriptSrc: "https://8x8.vc/vpaas-magic-cookie-xxx/external_api.js"}
function parseJaasDomain(domain) {
  try {
    const u = new URL(domain);
    const appId = u.pathname.replace(/^\//, "").replace(/\/$/, "");
    return {
      host: u.host,
      appId,
      scriptSrc: `${u.origin}/${appId}/external_api.js`,
    };
  } catch {
    return null;
  }
}

export function JitsiCall({
  roomName,
  displayName,
  email,
  jwt,
  domain,       // https://8x8.vc/{app_id}
  autoJoin = true,
  onLeave,
  onApiReady,
  hipaaMode,
}) {
  const containerRef = useRef(null);
  const apiRef = useRef(null);
  const [status, setStatus] = useState("initial"); // initial | loading | joined | error | unconfigured

  const notConfigured = !jwt || !domain;

  const hangup = useCallback(() => {
    try { apiRef.current?.executeCommand("hangup"); } catch { /* ignore */ }
    onLeave?.();
  }, [onLeave]);

  const toggleAudio = useCallback(() => {
    try { apiRef.current?.executeCommand("toggleAudio"); } catch { /* ignore */ }
  }, []);
  const toggleVideo = useCallback(() => {
    try { apiRef.current?.executeCommand("toggleVideo"); } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    if (notConfigured || !autoJoin || !containerRef.current) return undefined;
    let cancelled = false;
    const parsed = parseJaasDomain(domain);
    if (!parsed) {
      setStatus("error");
      return undefined;
    }
    setStatus("loading");
    let apiInstance = null;
    loadExternalApi(parsed.scriptSrc)
      .then((JitsiMeetExternalAPI) => {
        if (cancelled || !JitsiMeetExternalAPI || !containerRef.current) return;
        // Room name for JaaS must be prefixed with the tenant app id.
        const jaasRoom = `${parsed.appId}/${roomName}`;
        apiInstance = new JitsiMeetExternalAPI(parsed.host, {
          roomName: jaasRoom,
          parentNode: containerRef.current,
          jwt,
          userInfo: { displayName, email },
          configOverwrite: {
            // Trust the tokens for permissions — don't ask users again.
            prejoinPageEnabled: false,
            disableDeepLinking: true,
            startWithAudioMuted: false,
            startWithVideoMuted: false,
            // We render our own hangup button in the parent Stage.
            toolbarButtons: [
              "microphone", "camera", "chat", "tileview",
              "participants-pane", "raisehand", "settings",
            ],
          },
          interfaceConfigOverwrite: {
            MOBILE_APP_PROMO: false,
            SHOW_JITSI_WATERMARK: false,
            SHOW_CHROME_EXTENSION_BANNER: false,
          },
        });
        apiRef.current = apiInstance;

        apiInstance.addEventListener("videoConferenceJoined", () => {
          if (!cancelled) setStatus("joined");
        });
        apiInstance.addEventListener("readyToClose", () => {
          onLeave?.();
        });
      })
      .catch(() => {
        if (!cancelled) setStatus("error");
      });

    return () => {
      cancelled = true;
      try { apiInstance?.dispose(); } catch { /* ignore */ }
      apiRef.current = null;
    };
     
  }, [roomName, jwt, domain, autoJoin]);

  // Publish the imperative API upstream so ConsultationStage's control bar works.
  useEffect(() => {
    onApiReady?.({ toggleAudio, toggleVideo, hangup });
  }, [toggleAudio, toggleVideo, hangup, onApiReady]);

  if (notConfigured) {
    return (
      <div className="w-full h-full min-h-[420px] rounded-3xl bg-stone-900 text-white flex flex-col items-center justify-center gap-3 p-6 text-center" data-testid="jitsi-call-unconfigured">
        <ShieldAlert className="h-8 w-8 text-amber-400" />
        <div className="font-serif text-xl">We can&apos;t start the video right now</div>
        <p className="text-sm text-white/70 max-w-sm leading-relaxed">
          Please continue by message, or ask the front desk to reach your doctor.
          We&apos;re working on it — try again in a few minutes.
        </p>
      </div>
    );
  }

  return (
    <div className="relative w-full h-full min-h-[420px] bg-stone-900" data-testid="jitsi-call-shell">
      <div
        ref={containerRef}
        className="w-full h-full [&_iframe]:w-full [&_iframe]:h-full [&_iframe]:border-0"
        data-testid="jitsi-call-container"
      />
      {status === "loading" && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-stone-900/80 text-white">
          <Loader2 className="h-6 w-6 animate-spin" />
          <div className="text-sm">Connecting to Jitsi…</div>
        </div>
      )}
      {status === "error" && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-stone-900/90 text-white p-6 text-center">
          <ShieldAlert className="h-6 w-6 text-amber-400" />
          <div className="font-serif text-lg">Couldn&apos;t reach Jitsi</div>
          <p className="text-sm text-white/70 max-w-sm">
            The external SDK failed to load. Check network connectivity or fall back to LiveKit.
          </p>
        </div>
      )}
      {status === "joined" && (
        <div className="absolute top-3 left-3 z-10 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest px-2.5 py-1 rounded-full bg-black/40 border border-white/10 text-white/90"
             data-testid="jitsi-hipaa-badge">
          {hipaaMode ? <ShieldCheck className="h-3 w-3 text-emerald-300" /> : <VideoIcon className="h-3 w-3" />}
          {hipaaMode ? "Encrypted in transit · HIPAA" : "Encrypted in transit"}
        </div>
      )}
    </div>
  );
}
